import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  fetchMe,
  leaveAccount as apiLeaveAccount,
  logout as apiLogout,
  switchAccount as apiSwitchAccount,
  type AuthAccount,
  type AuthProperty,
  type AuthUser,
  type MeResponse,
  type Role,
} from './api';
import { getActivePropertyId, setActivePropertyId } from './auth';

/**
 * Single source of truth for "who is the user, what account are
 * they in, what property are they viewing." Populated by GET
 * /api/auth/me on mount; mutated by login / logout / switch flows.
 *
 * Status:
 *   loading        — still resolving the cookie via /me
 *   authenticated  — user + account + properties + role known
 *   unauthenticated — no valid session; render auth pages or
 *                     redirect via <RequireAuth>
 */

type Status = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthState {
  status: Status;
  user: AuthUser | null;
  account: AuthAccount | null;
  accounts: AuthAccount[];
  properties: AuthProperty[];
  activeProperty: AuthProperty | null;
  role: Role | null;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
  switchAccount: (accountId: string) => Promise<void>;
  /** Leave the active account. Re-mints the session for a remaining account. */
  leaveAccount: () => Promise<void>;
  switchProperty: (propertyId: string) => void;
  /**
   * Used by the SignIn / Register / AcceptInvite pages to feed the
   * just-set session into the context without an extra round-trip
   * (the auth endpoints already return a populated me-shape).
   */
  hydrate: (me: MeResponse) => void;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<Status>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [account, setAccount] = useState<AuthAccount | null>(null);
  const [accounts, setAccounts] = useState<AuthAccount[]>([]);
  const [properties, setProperties] = useState<AuthProperty[]>([]);
  const [role, setRole] = useState<Role | null>(null);
  const [activePropertyId, setActivePropertyIdState] = useState<string | null>(
    () => getActivePropertyId(),
  );

  const applyMe = useCallback((me: MeResponse) => {
    setUser(me.user);
    setAccount(me.activeAccount);
    setAccounts(me.accounts);
    setProperties(me.properties);
    setRole(me.role);
    // Reconcile active property: keep the stored one if it's still in
    // the list; otherwise default to the first.
    const stored = getActivePropertyId();
    const stillValid = stored && me.properties.find((p) => p.id === stored);
    const next = stillValid
      ? stored!
      : me.properties[0]?.id ?? null;
    setActivePropertyIdState(next);
    setActivePropertyId(next);
    setStatus('authenticated');
  }, []);

  const refresh = useCallback(async () => {
    try {
      const me = await fetchMe();
      applyMe(me);
    } catch {
      setStatus('unauthenticated');
      setUser(null);
      setAccount(null);
      setAccounts([]);
      setProperties([]);
      setRole(null);
    }
  }, [applyMe]);

  const signOut = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      /* server-side may have cleared already */
    }
    setActivePropertyId(null);
    setStatus('unauthenticated');
    setUser(null);
    setAccount(null);
    setAccounts([]);
    setProperties([]);
    setRole(null);
    qc.clear();
  }, [qc]);

  const switchAccount = useCallback(
    async (id: string) => {
      await apiSwitchAccount(id);
      qc.clear();
      // Force a fresh /me — properties + role change with the
      // active account.
      await refresh();
    },
    [qc, refresh],
  );

  const leaveAccount = useCallback(async () => {
    await apiLeaveAccount();
    setActivePropertyId(null);
    setActivePropertyIdState(null);
    qc.clear();
    await refresh();
  }, [qc, refresh]);

  const switchProperty = useCallback(
    (propertyId: string) => {
      if (propertyId === activePropertyId) return;
      setActivePropertyId(propertyId);
      setActivePropertyIdState(propertyId);
      // Clear every property-scoped query so the new property doesn't
      // inherit stale data. The whole-cache nuke is fine — datasets
      // are small and the user just made an explicit context switch.
      qc.clear();
    },
    [activePropertyId, qc],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const activeProperty = useMemo(
    () => properties.find((p) => p.id === activePropertyId) ?? null,
    [properties, activePropertyId],
  );

  const value = useMemo<AuthState>(
    () => ({
      status,
      user,
      account,
      accounts,
      properties,
      activeProperty,
      role,
      refresh,
      signOut,
      switchAccount,
      leaveAccount,
      switchProperty,
      hydrate: applyMe,
    }),
    [status, user, account, accounts, properties, activeProperty, role, refresh, signOut, switchAccount, leaveAccount, switchProperty, applyMe],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>');
  return v;
}
